import React from 'react'
import { Button, Input, Pagination, Skeleton } from '@/shared/ui'
import { Search } from 'lucide-react'
import { listFinanceMonthlyPayments, type FinancePaymentItem } from '../services/financeReportsService'
import { FinancePaymentsTable } from './FinancePaymentsTable'

type Props = {
  monthStartDate: string
  refreshKey: number
}

export function FinancePaymentsTab({ monthStartDate, refreshKey }: Props) {
  const [items, setItems] = React.useState<FinancePaymentItem[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(50)
  const [search, setSearch] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const res = await listFinanceMonthlyPayments({ monthStartDate, search, page, pageSize })
      setItems(res.items)
      setTotal(res.total)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar pagamentos')
      setItems([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => { setPage(1) }, [monthStartDate])
  React.useEffect(() => { load() }, [monthStartDate, refreshKey, page])

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize))

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input
            placeholder="Buscar por cliente ou e-mail"
            value={search}
            onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setPage(1); load() }}
            disabled={loading}
          >
            Aplicar filtro
          </Button>
          <Button onClick={() => load()} disabled={loading}>
            Atualizar
          </Button>
        </div>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-4">
        {loading && !items.length ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <FinancePaymentsTable items={items} />
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-foreground/70">
          Total: {Number(total || 0).toLocaleString('pt-BR')}
        </div>
        <Pagination page={page} total={totalPages} onChange={setPage} />
      </div>
    </div>
  )
}

